import { Platform } from 'react-native';

/**
 * Lightweight SQLite cache for books and insights (modern async API).
 * - Stores FireBook / FireInsight JSON as TEXT
 * - Tracks cachedAt and lastAccess for LRU eviction
 * - Caps each table at 1000 rows; evicts least recently accessed
 * - Eviction triggered at most once per day (on first access after interval)
 * 
 * Web: No-op implementation (SQLite not available on web)
 * Native: Full SQLite implementation
 */

const DB_NAME = 'cache.db';
const MAX_BOOK_ROWS = 1000;
const MAX_INSIGHT_ROWS = 1000;
const CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // once per day

// Only import SQLite on native platforms
let SQLite: typeof import('expo-sqlite') | null = null;
if (Platform.OS !== 'web') {
  try {
    SQLite = require('expo-sqlite');
  } catch (error) {
    console.warn('Failed to load expo-sqlite:', error);
  }
}

let db: any = null;
let lastCleanupAt = 0;

const openDb = async (): Promise<any> => {
  if (Platform.OS === 'web' || !SQLite) {
    throw new Error('SQLite not available on web platform');
  }
  if (!db) {
    db = await SQLite.openDatabaseAsync(DB_NAME);
  }
  return db;
};

const initSchema = async () => {
  const database = await openDb();
  
  await database.execAsync(`
    CREATE TABLE IF NOT EXISTS books (
      id TEXT NOT NULL,
      languageCode TEXT NOT NULL,
      data TEXT NOT NULL,
      cachedAt INTEGER NOT NULL,
      lastAccess INTEGER NOT NULL,
      PRIMARY KEY (id, languageCode)
    );
  `);

  await database.execAsync(`CREATE INDEX IF NOT EXISTS idx_books_lastAccess ON books(lastAccess);`);

  await database.execAsync(`
    CREATE TABLE IF NOT EXISTS insights (
      id TEXT NOT NULL,
      bookId TEXT NOT NULL,
      languageCode TEXT NOT NULL,
      data TEXT NOT NULL,
      cachedAt INTEGER NOT NULL,
      lastAccess INTEGER NOT NULL,
      PRIMARY KEY (id, languageCode)
    );
  `);

  await database.execAsync(`CREATE INDEX IF NOT EXISTS idx_insights_book ON insights(bookId);`);
  await database.execAsync(`CREATE INDEX IF NOT EXISTS idx_insights_lastAccess ON insights(lastAccess);`);
};

const maybeCleanup = async () => {
  const now = Date.now();
  if (now - lastCleanupAt < CLEANUP_INTERVAL_MS) return;
  lastCleanupAt = now;

  console.log('🧹 Running SQLite cache cleanup (once per day)...');
  await enforceCap('books', MAX_BOOK_ROWS);
  await enforceCap('insights', MAX_INSIGHT_ROWS);
};

const enforceCap = async (table: 'books' | 'insights', cap: number) => {
  const database = await openDb();
  
  // Count rows
  const countResult = await database.getFirstAsync<{ cnt: number }>(
    `SELECT COUNT(*) as cnt FROM ${table}`
  );
  const count = countResult?.cnt || 0;
  
  if (count <= cap) return;
  
  const toDelete = count - cap;
  console.log(`🗑️ Evicting ${toDelete} oldest ${table} (${count} → ${cap})`);
  
  // Delete oldest rows
  await database.execAsync(`
    DELETE FROM ${table}
    WHERE rowid IN (
      SELECT rowid FROM ${table}
      ORDER BY lastAccess ASC
      LIMIT ${toDelete}
    )
  `);
};

export interface SQLiteCacheService {
  init: () => Promise<void>;
  getBook: (id: string, languageCode: string) => Promise<any | null>;
  getBooks: (ids: string[], languageCode: string) => Promise<any[]>;
  saveBooks: (books: any[], languageCode: string) => Promise<void>;
  getInsights: (bookId: string, languageCode: string) => Promise<any[] | null>;
  saveInsights: (insights: any[], languageCode: string) => Promise<void>;
  deleteBook: (id: string, languageCode: string) => Promise<void>;
  deleteInsightsForBook: (bookId: string, languageCode: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

// Web-compatible no-op implementation
const createNoOpService = (): SQLiteCacheService => ({
  init: async () => {
    console.log('ℹ️ SQLite cache skipped on web platform');
  },
  getBook: async () => null,
  getBooks: async () => [],
  saveBooks: async () => {},
  getInsights: async () => null,
  saveInsights: async () => {},
  deleteBook: async () => {},
  deleteInsightsForBook: async () => {},
  clearAll: async () => {},
});

// Native SQLite implementation
const createSQLiteService = (): SQLiteCacheService => ({
  init: async () => {
    try {
      await initSchema();
      await maybeCleanup();
      console.log('✅ SQLite cache initialized');
    } catch (error) {
      console.error('❌ SQLite init failed:', error);
      throw error;
    }
  },

  getBook: async (id: string, languageCode: string) => {
    try {
      const database = await openDb();
      const row = await database.getFirstAsync<{ data: string }>(
        `SELECT data FROM books WHERE id = ? AND languageCode = ? LIMIT 1`,
        [id, languageCode]
      );
      
      if (!row) return null;
      
      // Update lastAccess
      await database.runAsync(
        `UPDATE books SET lastAccess = ? WHERE id = ? AND languageCode = ?`,
        [Date.now(), id, languageCode]
      );
      
      return JSON.parse(row.data);
    } catch (error) {
      console.error('SQLite getBook error:', error);
      return null;
    }
  },

  getBooks: async (ids: string[], languageCode: string) => {
    if (ids.length === 0) return [];
    
    try {
      const database = await openDb();
      const placeholders = ids.map(() => '?').join(',');
      const rows = await database.getAllAsync<{ id: string; data: string }>(
        `SELECT id, data FROM books WHERE languageCode = ? AND id IN (${placeholders})`,
        [languageCode, ...ids]
      );
      
      if (rows.length === 0) return [];
      
      const books = rows.map(row => JSON.parse(row.data));
      const foundIds = rows.map(row => row.id);
      
      // Update lastAccess for found rows
      if (foundIds.length > 0) {
        const updatePlaceholders = foundIds.map(() => '?').join(',');
        await database.runAsync(
          `UPDATE books SET lastAccess = ? WHERE languageCode = ? AND id IN (${updatePlaceholders})`,
          [Date.now(), languageCode, ...foundIds]
        );
      }
      
      return books;
    } catch (error) {
      console.error('SQLite getBooks error:', error);
      return [];
    }
  },

  saveBooks: async (books: any[], languageCode: string) => {
    if (!books || books.length === 0) return;
    
    try {
      const database = await openDb();
      const now = Date.now();
      
      // Insert books one by one (modern API doesn't have transaction batching like legacy)
      for (const book of books) {
        if (!book?.id) continue;
        
        await database.runAsync(
          `INSERT OR REPLACE INTO books (id, languageCode, data, cachedAt, lastAccess)
           VALUES (?, ?, ?, ?, ?)`,
          [book.id, languageCode, JSON.stringify(book), now, now]
        );
      }
      
      await enforceCap('books', MAX_BOOK_ROWS);
    } catch (error) {
      console.error('SQLite saveBooks error:', error);
    }
  },

  getInsights: async (bookId: string, languageCode: string) => {
    try {
      const database = await openDb();
      const rows = await database.getAllAsync<{ data: string }>(
        `SELECT data FROM insights WHERE languageCode = ? AND bookId = ?`,
        [languageCode, bookId]
      );
      
      if (rows.length === 0) return null;
      
      const insights = rows.map(row => JSON.parse(row.data));
      
      // Update lastAccess
      await database.runAsync(
        `UPDATE insights SET lastAccess = ? WHERE languageCode = ? AND bookId = ?`,
        [Date.now(), languageCode, bookId]
      );
      
      return insights;
    } catch (error) {
      console.error('SQLite getInsights error:', error);
      return null;
    }
  },

  saveInsights: async (insights: any[], languageCode: string) => {
    if (!insights || insights.length === 0) return;
    
    try {
      const database = await openDb();
      const now = Date.now();
      
      for (const insight of insights) {
        if (!insight?.id || !insight?.bookId) continue;
        
        await database.runAsync(
          `INSERT OR REPLACE INTO insights (id, bookId, languageCode, data, cachedAt, lastAccess)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [insight.id, insight.bookId, languageCode, JSON.stringify(insight), now, now]
        );
      }
      
      await enforceCap('insights', MAX_INSIGHT_ROWS);
    } catch (error) {
      console.error('SQLite saveInsights error:', error);
    }
  },

  deleteBook: async (id: string, languageCode: string) => {
    try {
      const database = await openDb();
      await database.runAsync(
        `DELETE FROM books WHERE id = ? AND languageCode = ?`,
        [id, languageCode]
      );
    } catch (error) {
      console.error('SQLite deleteBook error:', error);
    }
  },

  deleteInsightsForBook: async (bookId: string, languageCode: string) => {
    try {
      const database = await openDb();
      await database.runAsync(
        `DELETE FROM insights WHERE bookId = ? AND languageCode = ?`,
        [bookId, languageCode]
      );
    } catch (error) {
      console.error('SQLite deleteInsightsForBook error:', error);
    }
  },

  clearAll: async () => {
    try {
      const database = await openDb();
      await database.execAsync(`DELETE FROM books`);
      await database.execAsync(`DELETE FROM insights`);
      console.log('🧹 Cleared all SQLite cache');
    } catch (error) {
      console.error('SQLite clearAll error:', error);
    }
  },
});

// Export platform-specific implementation
export const sqliteCacheService: SQLiteCacheService = Platform.OS === 'web' 
  ? createNoOpService()
  : createSQLiteService();
